import { CalendarDate, fromDate, getLocalTimeZone } from "@internationalized/date";

type InputDate =
	| Date
	| string
	| CalendarDate
	| { year: number; month: number; day: number }
	| { target: { value: string } }
	| { toDate: () => Date }
	| { _seconds: number }
	| number
	| null
	| undefined;

type InputRange = { start: InputDate; end: InputDate } | { from: InputDate; to: InputDate };

type Normalized = CalendarDate | { start: CalendarDate; end: CalendarDate } | null;

/**
 * Convert any date-like value to CalendarDate
 */
function toCalendarDate(value: InputDate): CalendarDate | null {
	if (!value) return null;

	// Already a CalendarDate
	if (value instanceof CalendarDate) return value;

	// Get Date object from various formats
	let date: Date | null = null;

	if (value instanceof Date) {
		date = value;
	} else if (typeof value === "string") {
		date = new Date(value);
	} else if (typeof value === "number") {
		date = new Date(value);
	} else if (typeof value === "object") {
		// Firestore timestamp with toDate method
		if ("toDate" in value && typeof value.toDate === "function") {
			try {
				date = value.toDate();
			} catch {
				return null;
			}
		}
		// Firestore _seconds format
		else if ("_seconds" in value) {
			date = new Date(value._seconds * 1000);
		}
		// Input event
		else if ("target" in value && value.target && "value" in value.target) {
			const dateString = (value.target as { value: string }).value;
			date = dateString ? new Date(dateString) : null;
		}
		// CalendarDate-like object
		else if ("year" in value && "month" in value && "day" in value) {
			try {
				return new CalendarDate(value.year, value.month, value.day);
			} catch {
				return null;
			}
		}
	}

	// Convert Date to CalendarDate
	if (date && !isNaN(date.getTime())) {
		try {
			const zoned = fromDate(date, getLocalTimeZone());
			return new CalendarDate(zoned.year, zoned.month, zoned.day);
		} catch {
			return null;
		}
	}

	return null;
}

/**
 * Normalize date or date range
 */
function normalizeDate(value: InputDate | InputRange): Normalized {
	if (!value) return null;

	// Handle range objects
	if (typeof value === "object" && ("start" in value || "from" in value)) {
		const startValue = "start" in value ? value.start : (value as { from: InputDate }).from;
		const endValue = "end" in value ? (value as { end: InputDate }).end : (value as { to: InputDate }).to;

		const start = toCalendarDate(startValue);
		const end = toCalendarDate(endValue);

		if (start && end) return { start, end };
		return start; // Return single date if only start is valid
	}

	// Handle single date
	return toCalendarDate(value as InputDate);
}

export { normalizeDate, toCalendarDate as normalizeSingleDate, type InputDate, type InputRange, type Normalized };
